import React, { useMemo } from 'react';
import classnames from 'classnames';
import moment from 'moment';
import { Button, TextArea, Input, DatePicker } from 'tea-component';
import GPSelect from '@gp/components/Select';
import DatePickerWrapper from '@gp/components/DatePickerWrapper';
import NewFeatureTipsBubble from '@gp/components/NewFeatureTipsBubble';
import OperationBtns from '@gp/components/OperationBtns';
import { generateUUID } from '@gp/utils/common';
import {
  CONSTANT_TEXTS,
  STATIC_GR_ACTION_STATUSES,
  STATIC_GR_ACTION_STATUS_NAMES,
  ACTION_ITEM_STATUS_NAMES_MAPPING,
  StaticActionItemCodes,
} from '@gp/constants/const';
import FollowerRange from '@gp/components/FollowerRange';
import type { IBaseProps } from '@home/types/common';
import type { IGRAction } from '@gp/pages/Pipeline/ProjectDetails/Milestone/types';
import { getActionItemCategoryOptions } from '@gp/pages/Pipeline/utils';
import './index.scss';

interface IGRActionItemsFormControlProps extends IBaseProps {
  // 是否开启Status下拉筛选器的新功能强提醒交互
  newFeatureTipsIntersection?: boolean;
  // Status下拉筛选器的新功能强提醒的key
  newFeatureTipsKey?: string;
  value?: IGRAction[];
  onChange?: (payload: IGRAction[]) => void;
}

const GR_ACTION_ITEM_STATUS_SELECT_TIPS = `${CONSTANT_TEXTS.CANCELED} and ${CONSTANT_TEXTS.ON_HOLD} status are included in the selection.`;

const GRActionItemsFormControl: React.FC<IGRActionItemsFormControlProps> = (props) => {
  const {
    style,
    className,
    newFeatureTipsIntersection = false,
    newFeatureTipsKey = '',
    value: grActions = [],
    onChange,
    setIsChangeValue,
    actionItemCategory,
  } = props;

  const isGrActionsNoData = !Array.isArray(grActions) || !grActions.length;

  const handleGRActionChange = (id?: string, payload?: Partial<IGRAction>) => {
    if (!id) return;
    setIsChangeValue?.(true);
    const grActionIdx = grActions.findIndex((grAction) => grAction?.id === id);
    const grAction = grActions[grActionIdx];
    const grActionTmp = { ...grAction, ...payload };
    const grActionsTmp = [...grActions.slice(0, grActionIdx), grActionTmp, ...grActions.slice(grActionIdx + 1)];
    onChange?.(grActionsTmp);
  };

  const handleAddGRAction = () => {
    setIsChangeValue?.(true);
    const id = generateUUID();
    const grActionTmp: IGRAction = {
      id,
      due_date: '',
      user: '',
      state: ACTION_ITEM_STATUS_NAMES_MAPPING[StaticActionItemCodes.NotStarted],
      action_content: '',
      state_comment: '',
    };
    const grActionsTmp = [...grActions, grActionTmp];
    onChange?.(grActionsTmp);
  };

  const handleDeleteGRAction = (id?: string) => {
    if (!id) return;
    setIsChangeValue?.(true);
    const grActionsTmp = grActions.filter((item) => item?.id !== id);
    onChange?.(grActionsTmp);
  };

  return (
    <div
      style={style}
      className={classnames('dbg-gr-action-items-form-control dbg-gr-action-items-form-box__list', className, {
        empty: isGrActionsNoData,
      })}
    >
      {isGrActionsNoData && (
        <Button
          type="link"
          onClick={(e) => {
            e?.stopPropagation();
            handleAddGRAction();
          }}
        >
          Add
        </Button>
      )}
      {isGrActionsNoData ||
        grActions?.map((grAction, grActionIdx) => {
          const grActionId = grAction?.id;
          const grActionDate = grAction?.due_date;
          const isCategoryValid = !!grAction?.category;
          const grActionStatus = grAction?.state;

          const isEndData = Array.isArray(grActions) && grActions?.length > 0 && grActionIdx === grActions?.length - 1;
          const grActionStatusSelect = (
            <GPSelect
              // 改字段非必填，不需要错误样式
              // className={classnames({ 'dbg-is-error': !grActionStatus })}
              label="Status"
              clearable={false}
              options={STATIC_GR_ACTION_STATUSES}
              value={grActionStatus}
              onChange={(payload) => {
                handleGRActionChange(grActionId, { state: payload });
              }}
              button={
                <>
                  <span className="select-button-text">Status</span>
                  <span className="select-button-value">
                    {STATIC_GR_ACTION_STATUS_NAMES[grActionStatus?.toLowerCase()]}
                  </span>
                </>
              }
            />
          );
          return (
            <div
              key={grActionId}
              className={classnames('dbg-gr-action-items-form-box__item', {
                'dbg-is-error': !grActionDate,
              })}
            >
              <div className="dbg-gr-action-items-form-box__item-label">Item {grActionIdx + 1}</div>
              <div className="dbg-gr-action-items-form-box__item-content">
                <FollowerRange
                  // className={classnames({ 'dbg-is-error': !isCategoryValid })}
                  label="Category"
                  placeholder="Please select"
                  customizePlaceholder="Please input Risks name"
                  options={getActionItemCategoryOptions(actionItemCategory)}
                  value={grAction?.category}
                  onChange={(payload) => {
                    handleGRActionChange(grActionId, { category: payload });
                  }}
                />
                <Input
                  className={classnames('dbg-gr-action-items-form-control__input', {
                    // 改字段非必填，不需要错误样式
                    // 'dbg-is-error': !grAction?.user,
                  })}
                  placeholder="Please input Owner"
                  value={grAction?.user}
                  onChange={(payload) => {
                    handleGRActionChange(grActionId, { user: payload });
                  }}
                />
                <div className="dbg-project-details-edit-divider">-</div>
                {/* 日期选择 */}
                <DatePickerWrapper className={classnames({ 'dbg-is-error': !grActionDate })} label="DUE">
                  <DatePicker
                    placeholder="Select Date"
                    value={grActionDate ? moment(grActionDate) : undefined}
                    onChange={(payload) => {
                      const date = payload?.format('YYYY-MM-DD');
                      handleGRActionChange(grActionId, { due_date: date });
                    }}
                  />
                </DatePickerWrapper>
                {grActionIdx !== 0 || !newFeatureTipsIntersection ? (
                  grActionStatusSelect
                ) : (
                  <NewFeatureTipsBubble
                    overlayStyle={{ zIndex: 1002 }}
                    newFeatureTipsKey={newFeatureTipsKey}
                    newFeatureTipsContent={GR_ACTION_ITEM_STATUS_SELECT_TIPS}
                  >
                    {grActionStatusSelect}
                  </NewFeatureTipsBubble>
                )}
                <div className="dbg-gr-action-items-form-control__textarea-box">
                  <TextArea
                    // 改字段非必填，不需要错误样式
                    // className={classnames('', { 'dbg-is-error': !grAction?.action_content })}
                    placeholder="Please input comment"
                    className="dbg-gr-action-items-comment-left"
                    value={grAction?.action_content}
                    onChange={(payload) => {
                      handleGRActionChange(grActionId, { action_content: payload });
                    }}
                  />
                  {/* <div className="dbg-gr-action-items-form-control__spliter"> </div> */}
                  <TextArea
                    // 改字段非必填，不需要错误样式
                    // className={classnames('', { 'dbg-is-error': !grAction?.state_comment })}
                    placeholder="Please input Status comment"
                    value={grAction?.state_comment}
                    onChange={(payload) => {
                      handleGRActionChange(grActionId, { state_comment: payload });
                    }}
                  />
                  {/* 操作按钮 */}
                  <OperationBtns
                    className="dbg-gr-action-items-form-control__operation-btns"
                    isShowAdd={isEndData}
                    isShowDelete
                    onAdd={handleAddGRAction}
                    onDelete={() => {
                      handleDeleteGRAction(grActionId);
                    }}
                  />
                </div>
              </div>
            </div>
          );
        })}
    </div>
  );
};

export default GRActionItemsFormControl;
